import React from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { saveAs } from 'file-saver';
import { Spin, Col, Radio, Drawer, Tooltip, Row, Space, Tag } from 'antd';
import { ChurnCloseSvgIcon, DashboardFilterIcon } from 'utils/icons';
import PropTypes from 'prop-types';
import Typography from 'antd/es/typography/Typography';
import { DownloadOutlined, InfoCircleOutlined, EyeOutlined } from '@ant-design/icons';
import BarChartGraph from 'components/BarchartGraph';
import AreaChartGraph from 'components/AreaChartGraph';
import DashBoardSummaryCard from 'components/DashBoardSummaryCard';
import CustomSkeleton from 'components/CustomSkeleton';
import EbzButton from 'components/EbzButton';
import EbzTable from 'components/EbzTable';
import {
  PAYMENT_MODE_COLUMNS,
  PAYMENT_MODE_COLUMNS_EXPORT,
  PREVIOUS_QUARTER,
  ANNUALLY,
  getChurnThreeMonthColumn,
  getChurnQuarterColumn,
  CHURN_QUARTER_TAG,
  CHURN_MONTH_TAG,
  CHURN_YAXIS_LABEL,
  CHURN_XAXIS_LABEL
} from 'constants/dashboard';
import './DashBoardPage.scss';
import TopMerchantsBarChart from 'components/TopMerchantsBarChart';
import DashBoardFilterPage from './DashboardFilter';
import DashboardCommomFooter from './DashboardCommomFooter';

//  this is dashbord first component that is transaction graph
//  In this transation success and failure graph will be shown
//  Transaction Wise payment mode wise table will be shown
//  In this chrun graph  has shown
function TransactionPage({
  hasSummaryData,
  summary,
  transactionLoading,
  transactionGraphData,
  hasPaymentModeData,
  handleChurnRange,
  paymentModeLoading,
  churnGraphLoading,
  churnTableLoading,
  churnGarphData,
  churnViewMore,
  churnTableData,
  churnGraphKeyName,
  churnTableKeyName,
  churnRange,
  churnTableParams,
  handleChurnTableChange,
  handleChurnViewMore,
  filterTransactionGraphData,
  dashBoardForm,
  RANGE_PRESETS_DAYS,
  disabledDate,
  productList,
  merchants,
  transactionBarChartData,
  transactionBarChartKey,
  hasTopMerchantTransaction,
  fetchMerchantList,
  merchantLoading,
  setMerchantPagination,
  resetDashboardForm,
  merchantTransactionListRef,
  selectedMerchantTotalCounts,
  handleOpen,
  openModal,
  submerchants,
  fetchSubMerchantList,
  submerchantLoading,
  setSubmerchantPagination,
  submerchantTransactionListRef,
  selectedSubmerchantTotalCounts
}) {
  const canExportDashboard = useSelector(
    (state) => state.user.planPermissions.can_export_dashboard
  );

  function convertToCSV(data) {
    const header = Object.values(PAYMENT_MODE_COLUMNS_EXPORT).join(',');
    const rows = data.map((obj) => Object.values(obj).join(',')).join('\n');
    return `${header}\n${rows}`;
  }

  function handleDownload() {
    const csvString = convertToCSV(transactionGraphData?.paymentModeData);
    const csvData = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    saveAs(csvData, 'PaymentModeWise.csv');
  }

  return (
    <>
      <Row gutter={[8, 8]} className="transaction-page-row">
        <Col span={24}>
          <div className="transaction-graph">
            <Row align="middle" justify="space-between" style={{ gap: '14px' }}>
              <Col>
                <Typography.Title style={{ fontSize: '20px', fontWeight: '500' }}>
                  Transaction Report
                </Typography.Title>
              </Col>
              <Col>
                <EbzButton
                  icon={
                    <DashboardFilterIcon style={{ fontSize: 16, color: 'var(--color-primary)' }} />
                  }
                  size="large"
                  style={{
                    display: 'flex',
                    border: '1px solid',
                    alignItems: 'center',
                    gap: 2,
                    borderColor: 'var(--color-primary)',
                    color: 'var(--color-primary)',
                    borderRadius: 4,
                    padding: '0 16px',
                    fontWeight: 500
                  }}
                  onClick={() => handleOpen()}
                >
                  <Typography.Text style={{ color: 'var(--color-primary)' }}>
                    Filters
                  </Typography.Text>
                </EbzButton>
              </Col>
            </Row>
            <Row gutter={[18, 18]}>
              <Col span={24} style={{ paddingTop: '22px' }}>
                {hasSummaryData ? (
                  <DashBoardSummaryCard dashBoardSummary={summary} summaryKey="TRANSACTION" />
                ) : (
                  <CustomSkeleton />
                )}
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Spin spinning={transactionLoading}>
                  <div style={{ marginTop: '4%' }}>
                    <Typography.Title style={{ fontSize: '20px', fontWeight: '500' }}>
                      Transaction Data
                    </Typography.Title>
                    <div className="transaction-area-chart">
                      <AreaChartGraph
                        data={transactionGraphData?.successRateData}
                        xDataKey="transaction_date"
                        graphFailureKey="failure_count"
                        graphSucessKey="success_count"
                      />
                    </div>
                  </div>
                </Spin>
              </Col>
            </Row>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}
            >
              <Space direction="horizontal" style={{ justifyContent: 'space-between' }}>
                <Space>
                  <Typography style={{ fontSize: '20px', fontWeight: '500' }}>
                    Payment Mode Wise Transaction
                  </Typography>
                </Space>
                <Space>
                  <EbzButton
                    style={{ color: 'var(--color-primary)', border: '1px solid', borderRadius: 4 }}
                    size="large"
                    onClick={() => handleDownload()}
                    disabled={!hasPaymentModeData || !canExportDashboard}
                    icon={<DownloadOutlined />}
                  >
                    <Typography.Text style={{ fontSize: '14px', color: 'var(--color-primary)' }}>
                      Export
                    </Typography.Text>
                  </EbzButton>
                </Space>
              </Space>
              <div className="payment-mode-table">
                <EbzTable
                  columns={PAYMENT_MODE_COLUMNS}
                  // eslint-disable-next-line no-underscore-dangle
                  rowKey={(r) => `${r.title}_type`}
                  dataSource={hasPaymentModeData ? transactionGraphData?.paymentModeData : []}
                  loading={paymentModeLoading}
                  pagination={false}
                  isPagination={false}
                  scroll={hasPaymentModeData && { x: 200, y: 440 }}
                  sticky
                  rowClassName="custom-row-hover"
                  size="small"
                  bordered
                />
              </div>
            </div>
          </div>
        </Col>
        <Col span={24} style={{ paddingLeft: '11px', paddingRight: '11px' }}>
          <Spin spinning={hasTopMerchantTransaction}>
            <Row className="top-merchants" gutter={[8, 16]}>
              <Col span={24}>
                <Space direction="horizontal" style={{ display: 'flex', gap: '29px' }}>
                  <Space>
                    <Typography style={{ fontSize: '20px', fontWeight: '500' }}>
                      Top 10 Merchants
                    </Typography>
                  </Space>
                  {transactionBarChartKey && (
                    <Space>
                      <Tag color="purple">
                        For {dayjs(transactionBarChartKey).format('MMMM YYYY')}
                      </Tag>
                    </Space>
                  )}
                </Space>
              </Col>
              <Col
                span={24}
                style={{
                  height: '400px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  maxWidth: '100%'
                }}
              >
                <TopMerchantsBarChart
                  barChartData={transactionBarChartData}
                  XaxisKey="Merchant ID"
                  YaxisKey="Transaction"
                  yAxislabel="top_transaction_merchant"
                />
              </Col>
            </Row>
          </Spin>
        </Col>
        {/* eslint-disable */}
        <Col span={24}>
          <Spin spinning={churnGraphLoading}>
            <div className="churn-main-container">
              <div className="churn-sub-container">
                <div className="churn-sub-option">
                  <div className="churn-text-tool">
                    <Typography style={{ fontSize: '20px', fontWeight: '500' }}>
                      Churn Indicator
                    </Typography>
                    <Tooltip
                      placement="top"
                      title="Churn Rate Calculated based on Transaction Count"
                    >
                      <InfoCircleOutlined style={{ color: 'grey', cursor: 'pointer' }} />
                    </Tooltip>
                  </div>
                  <Radio.Group onChange={handleChurnRange} value={churnRange}>
                    <Radio value={PREVIOUS_QUARTER}>{CHURN_MONTH_TAG}</Radio>
                    <Radio value={ANNUALLY}>{CHURN_QUARTER_TAG}</Radio>
                  </Radio.Group>
                </div>
                <div>
                  {churnGarphData?.length > 0 && (
                    <EbzButton
                      size="large"
                      onClick={() => handleChurnViewMore(true)}
                      style={{
                        color: 'var(--color-primary)',
                        border: '1px solid',
                        borderRadius: 4
                      }}
                      icon={<EyeOutlined />}
                    >
                      View More
                    </EbzButton>
                  )}
                  <Drawer
                    title={
                      <>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '15px'
                          }}
                        >
                          <Space style={{ gap: '50px' }}>
                            <Space>
                              <Typography.Text style={{ fontSize: '22px', fontWeight: '500' }}>
                                Churn Indicator
                              </Typography.Text>
                            </Space>
                            <Space>
                              <Typography.Text
                                style={{
                                  color: '#235FF0',
                                  fontFamily: 'DM Sans',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  backgroundColor: '#235FF0',
                                  borderRadius: '4px',
                                  padding: '6px 7px',
                                  background: 'rgba(81, 124, 227, 0.20)',
                                  lineHeight: '20px' /* 183.333% */
                                }}
                              >
                                {churnRange === PREVIOUS_QUARTER
                                  ? CHURN_MONTH_TAG
                                  : CHURN_QUARTER_TAG}
                              </Typography.Text>
                            </Space>
                          </Space>

                          <ChurnCloseSvgIcon
                            style={{
                              fontSize: '30px',
                              color: 'var(--color-primary)',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleChurnViewMore(false)}
                          />
                        </div>
                      </>
                    }
                    placement="right"
                    width={1300}
                    closable={false}
                    open={churnViewMore}
                    rootClassName="payout-drawer"
                  >
                    <EbzTable
                      // eslint-disable-next-line no-underscore-dangle
                      rowKey={(r) => `${r.name}_merchant`}
                      dataSource={churnViewMore ? churnTableData : []}
                      scroll={{ x: 1500, y: 2000 }}
                      loading={churnTableLoading}
                      pagination={churnTableParams.pagination}
                      onChange={handleChurnTableChange}
                      columns={
                        churnRange === PREVIOUS_QUARTER
                          ? getChurnThreeMonthColumn(churnTableKeyName)
                          : getChurnQuarterColumn(churnTableKeyName)
                      }
                    />
                  </Drawer>
                </div>
              </div>
              <div className="churn-bar-chart">
                <BarChartGraph
                  barKeys={churnGraphKeyName}
                  xDataKey="name"
                  xAxisLabelLength={CHURN_XAXIS_LABEL}
                  yAxislabel={{
                    value: CHURN_YAXIS_LABEL,
                    angle: -90,
                    offset: 50,
                    position: 'insideRight',
                    fill: 'black'
                  }}
                  data={churnGarphData}
                />
              </div>
            </div>
          </Spin>
        </Col>
      </Row>

      <Drawer
        title={
          <Space style={{ justifyContent: 'space-between', display: 'flex' }}>
            <Space>
              {' '}
              <Typography.Text style={{ fontSize: '20px', fontWeight: '700' }}>
                Filters
              </Typography.Text>
            </Space>
            <Space>
              <ChurnCloseSvgIcon onClick={() => handleOpen()} style={{ fontSize: '25px' }} />
            </Space>
          </Space>
        }
        placement="right"
        width={500}
        closable={false}
        open={openModal}
        onClose={handleOpen}
        rootClassName="transaction-drawer"
        footer={
          <DashboardCommomFooter
            filterGraphData={filterTransactionGraphData}
            handleOpen={handleOpen}
            resetDashboardForm={resetDashboardForm}
            dashBoardForm={dashBoardForm}
          />
        }
      >
        <DashBoardFilterPage
          formName="transactionFilterForm"
          productList={productList}
          filterGraphData={filterTransactionGraphData}
          dashBoardForm={dashBoardForm}
          RANGE_PRESETS_DAYS={RANGE_PRESETS_DAYS}
          disabledDate={disabledDate}
          merchants={merchants}
          fetchMerchantList={fetchMerchantList}
          merchantLoading={merchantLoading}
          setMerchantPagination={setMerchantPagination}
          merchantTransactionListRef={merchantTransactionListRef}
          selectedMerchantTotalCounts={selectedMerchantTotalCounts}
          submerchants={submerchants}
          fetchSubMerchantList={fetchSubMerchantList}
          submerchantLoading={submerchantLoading}
          setSubmerchantPagination={setSubmerchantPagination}
          submerchantTransactionListRef={submerchantTransactionListRef}
          selectedSubmerchantTotalCounts={selectedSubmerchantTotalCounts}
        />
      </Drawer>
    </>
  );
}

TransactionPage.propTypes = {
  hasSummaryData: PropTypes.bool.isRequired,
  transactionLoading: PropTypes.bool.isRequired,
  hasPaymentModeData: PropTypes.bool.isRequired,
  paymentModeLoading: PropTypes.bool.isRequired,
  churnGraphLoading: PropTypes.bool.isRequired,
  churnTableLoading: PropTypes.bool.isRequired,
  churnViewMore: PropTypes.bool.isRequired,
  churnTableData: PropTypes.instanceOf(Array).isRequired,
  churnGraphKeyName: PropTypes.instanceOf(Array).isRequired,
  churnTableKeyName: PropTypes.instanceOf(Array).isRequired,
  churnGarphData: PropTypes.instanceOf(Array).isRequired,
  churnRange: PropTypes.string.isRequired,
  churnTableParams: PropTypes.instanceOf(Object).isRequired,
  summary: PropTypes.instanceOf(Object).isRequired,
  transactionGraphData: PropTypes.instanceOf(Object).isRequired,
  handleChurnRange: PropTypes.func.isRequired,
  handleChurnTableChange: PropTypes.func.isRequired,
  handleChurnViewMore: PropTypes.func.isRequired,
  filterTransactionGraphData: PropTypes.func.isRequired,
  dashBoardForm: PropTypes.instanceOf(Object).isRequired,
  RANGE_PRESETS_DAYS: PropTypes.instanceOf(Array).isRequired,
  disabledDate: PropTypes.func.isRequired,
  productList: PropTypes.instanceOf(Array).isRequired,
  merchants: PropTypes.instanceOf(Object).isRequired,
  transactionBarChartData: PropTypes.instanceOf(Array).isRequired,
  transactionBarChartKey: PropTypes.string.isRequired,
  hasTopMerchantTransaction: PropTypes.bool.isRequired,
  fetchMerchantList: PropTypes.func.isRequired,
  merchantLoading: PropTypes.bool.isRequired,
  setMerchantPagination: PropTypes.func.isRequired,
  resetDashboardForm: PropTypes.func.isRequired,
  merchantTransactionListRef: PropTypes.instanceOf(Object).isRequired,
  selectedMerchantTotalCounts: PropTypes.number.isRequired,
  handleOpen: PropTypes.func.isRequired,
  openModal: PropTypes.bool.isRequired,
  submerchants: PropTypes.instanceOf(Array).isRequired,
  fetchSubMerchantList: PropTypes.func.isRequired,
  submerchantLoading: PropTypes.bool.isRequired,
  setSubmerchantPagination: PropTypes.func.isRequired,
  submerchantTransactionListRef: PropTypes.instanceOf(Object).isRequired,
  selectedSubmerchantTotalCounts: PropTypes.number.isRequired
};
export default TransactionPage;
